import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import {
  Button,
  Card,
  CardBody,
  Col,
  Container,
  Input,
  Modal,
  ModalBody,
  ModalHeader,
  Row,
  Spinner,
} from "reactstrap";
import Breadcrumbs from "../../components/Common/Breadcrumb";
import {
  createLecture,
  createQuestion,
  deleteQuestion,
  listUnitQuestions,
  normQuestion,
  updateQuestion,
} from "./api";
import QuestionEditor, {
  makeOption,
  makeQuestion,
} from "./components/QuestionEditor";
import {
  ActionMenu,
  ConfirmDialog,
  CounterPill,
  EmptyState,
  ErrorState,
  Field,
  LoadingState,
} from "./components/Ui";
import { PERMISSIONS, can } from "./permissions";
import { useApiCall } from "./useApiCall";
import { validateQuestion } from "./validation";
import "./questionData.css";

/**
 * Flat view of every question inside one bank unit, reached from a course's
 * "Questions" button. Lectures are an internal grouping here - the admin
 * adds questions to the unit and the page picks (or creates) the lecture.
 */

const num = (v) => Number(v || 0);

const toPayload = (q, lectureId, index) => ({
  lecture_id: lectureId,
  question_text: q.question_text.trim(),
  question_type: q.question_type,
  explanation: q.explanation?.trim() || "",
  difficulty_level: q.difficulty_level,
  marks: q.marks === "" || q.marks == null ? 1 : Number(q.marks),
  status: q.status,
  display_order:
    q.display_order === "" || q.display_order == null ? index + 1 : Number(q.display_order),
  correct_answer: q.correct_answer?.trim?.() || "",
  options: (q.options || []).map((o, i) => ({
    option_text: o.option_text.trim(),
    is_correct: o.is_correct ? 1 : 0,
    display_order: i + 1,
  })),
});

const toDraft = (q) =>
  makeQuestion({
    key: String(q.question_id),
    question_id: q.question_id,
    lecture_id: q.lecture_id,
    question_text: q.question_text || "",
    question_type: q.question_type,
    explanation: q.explanation || "",
    difficulty_level: q.difficulty_level || "medium",
    marks: q.marks ?? 1,
    status: q.status,
    display_order: q.display_order ?? "",
    correct_answer: q.correct_answer || "",
    options: (q.options || []).map((o) =>
      makeOption(o.option_text || "", String(o.is_correct) === "1" || o.is_correct === true)
    ),
  });

const UnitQuestionsPage = () => {
  const { qbUnitId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const call = useApiCall();

  const courseName = location.state?.coursedata?.course_name;

  const [unit, setUnit] = useState(null);
  const [lectures, setLectures] = useState([]);
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [search, setSearch] = useState("");

  const [modal, setModal] = useState(null); // { mode, draft, lectureId }
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  document.title = unit?.unit_name
    ? `${unit.unit_name} | Questions`
    : "Unit Questions | Medicotoon";

  const fetchAll = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    const { ok, data, message } = await call(
      () => listUnitQuestions({ qb_unit_id: qbUnitId }),
      { silent: true }
    );
    if (!ok) {
      setLoadError(message || "Failed to load the unit's questions");
      setLoading(false);
      return;
    }
    setUnit(data);
    setLectures(Array.isArray(data?.lectures) ? data.lectures : []);
    setQuestions(
      (Array.isArray(data?.questions) ? data.questions : []).map(normQuestion)
    );
    setLoading(false);
  }, [call, qbUnitId]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return questions;
    return questions.filter((q) =>
      String(q.question_text || "").toLowerCase().includes(term)
    );
  }, [questions, search]);

  /* ---------------------------------------------------------------- */
  /* Add / edit                                                        */
  /* ---------------------------------------------------------------- */
  const openAdd = () => {
    setErrors({});
    setModal({
      mode: "add",
      draft: makeQuestion({ display_order: questions.length + 1 }),
      // Default to the first lecture; the picker only shows when there are several.
      lectureId: lectures[0]?.lecture_id ?? "",
    });
  };

  const openEdit = (question) => {
    setErrors({});
    setModal({
      mode: "edit",
      draft: toDraft(question),
      lectureId: question.lecture_id,
    });
  };

  /**
   * Questions must belong to a lecture. When the unit has none yet, one is
   * created silently, named after the unit, so the admin never has to think
   * about lectures from this screen.
   */
  const ensureLectureId = async () => {
    if (modal.lectureId) return modal.lectureId;
    if (lectures.length) return lectures[0].lecture_id;

    const { ok, data } = await call(
      () =>
        createLecture({
          unit_id: qbUnitId,
          name: unit?.unit_name || "Questions",
          description: "",
          display_order: 1,
          status: "active",
        }),
      { error: "Could not prepare the unit for questions" }
    );
    if (!ok) return null;
    const lectureId = data?.lecture_id;
    setLectures([{ lecture_id: lectureId, name: unit?.unit_name || "Questions" }]);
    return lectureId;
  };

  const submit = async () => {
    const validation = validateQuestion({ ...modal.draft, lecture_id: "pending" });
    setErrors(validation);
    if (Object.keys(validation).length) {
      toast.error("Please fix the highlighted fields");
      return;
    }

    setSaving(true);
    const lectureId = await ensureLectureId();
    if (!lectureId) {
      setSaving(false);
      return;
    }

    const payload = toPayload(modal.draft, lectureId, questions.length);
    const { ok } =
      modal.mode === "add"
        ? await call(() => createQuestion(payload), { success: "Question added" })
        : await call(
            () => updateQuestion({ ...payload, question_id: modal.draft.question_id }),
            { success: "Question updated" }
          );
    setSaving(false);

    if (ok) {
      setModal(null);
      fetchAll();
    }
  };

  const confirmDelete = async () => {
    setSaving(true);
    const { ok } = await call(() => deleteQuestion(deleteTarget.question_id), {
      success: "Question deleted",
    });
    setSaving(false);
    if (ok) {
      setDeleteTarget(null);
      fetchAll();
    }
  };

  /* ---------------------------------------------------------------- */
  /* Render                                                            */
  /* ---------------------------------------------------------------- */
  if (!can(PERMISSIONS.BANK_VIEW)) {
    return (
      <div className="page-content">
        <Container fluid>
          <Breadcrumbs title="Courses" breadcrumbItem="Unit Questions" />
          <EmptyState
            icon="bx bx-lock-alt"
            title="Access denied"
            description="You do not have permission to view questions."
          />
        </Container>
      </div>
    );
  }

  const showLecturePicker = lectures.length > 1;

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <Breadcrumbs
            title={courseName || unit?.bank_name || "Courses"}
            breadcrumbItem={unit?.unit_name ? `${unit.unit_name} > Questions` : "Questions"}
          />

          <Row>
            <Col lg={12}>
              <Card>
                <CardBody>
                  {loading ? (
                    <LoadingState label="Loading questions..." />
                  ) : loadError ? (
                    <ErrorState message={loadError} onRetry={fetchAll} />
                  ) : (
                    <>
                      <div className="qd-toolbar">
                        <div>
                          <Button
                            color="light"
                            size="sm"
                            className="mb-2"
                            onClick={() => navigate(-1)}
                          >
                            <i className="bx bx-arrow-back me-1" />
                            Back
                          </Button>
                          <h4 className="mb-1">{unit?.unit_name}</h4>
                          <div className="qd-toolbar-stats">
                            <CounterPill
                              icon="bx bx-help-circle"
                              count={questions.length}
                              label="questions"
                            />
                            <span className="qd-counter text-muted">
                              Bank: {unit?.bank_name}
                            </span>
                          </div>
                        </div>
                        <div className="qd-toolbar-actions">
                          {can(PERMISSIONS.QUESTION_CREATE) ? (
                            <Button color="primary" onClick={openAdd}>
                              <i className="bx bx-plus me-1" />
                              Add Question
                            </Button>
                          ) : null}
                        </div>
                      </div>

                      <Row className="g-2 mt-3">
                        <Col md={6}>
                          <Input
                            type="text"
                            placeholder="Search questions..."
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                          />
                        </Col>
                      </Row>

                      <div className="mt-3">
                        {!questions.length ? (
                          <EmptyState
                            icon="bx bx-help-circle"
                            title="No questions in this unit yet"
                            description="Add the first question to this unit."
                            action={
                              can(PERMISSIONS.QUESTION_CREATE) ? (
                                <Button color="primary" onClick={openAdd}>
                                  <i className="bx bx-plus me-1" />
                                  Add Question
                                </Button>
                              ) : null
                            }
                          />
                        ) : !filtered.length ? (
                          <EmptyState
                            icon="bx bx-search"
                            title="No matching questions"
                            description="Try a different search term."
                          />
                        ) : (
                          filtered.map((question, index) => (
                            <div className="qd-question-row" key={question.question_id}>
                              <div className="qd-question-index">{index + 1}</div>
                              <div className="qd-question-body">
                                <div className="qd-question-text">
                                  {question.question_text}
                                </div>
                                {showLecturePicker && question.lecture_name ? (
                                  <div className="qd-question-tags">
                                    <span className="badge bg-light text-dark">
                                      {question.lecture_name}
                                    </span>
                                  </div>
                                ) : null}
                                {question.options?.length ? (
                                  <ul className="qd-question-options">
                                    {question.options.map((o, i) => (
                                      <li
                                        key={o.option_id || i}
                                        className={o.is_correct ? "is-correct" : ""}
                                      >
                                        <i
                                          className={`bx ${
                                            o.is_correct ? "bx-check-circle" : "bx-circle"
                                          }`}
                                        />
                                        {o.option_text}
                                      </li>
                                    ))}
                                  </ul>
                                ) : null}
                              </div>
                              <div className="qd-node-actions">
                                <ActionMenu
                                  actions={[
                                    {
                                      key: "edit",
                                      label: "Edit",
                                      icon: "bx bx-edit",
                                      hidden: !can(PERMISSIONS.QUESTION_UPDATE),
                                      onClick: () => openEdit(question),
                                    },
                                    { key: "d1", divider: true },
                                    {
                                      key: "delete",
                                      label: "Delete",
                                      icon: "bx bx-trash",
                                      danger: true,
                                      hidden: !can(PERMISSIONS.QUESTION_DELETE),
                                      onClick: () => setDeleteTarget(question),
                                    },
                                  ]}
                                />
                              </div>
                            </div>
                          ))
                        )}
                      </div>
                    </>
                  )}
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>

      <Modal isOpen={!!modal} toggle={() => !saving && setModal(null)} centered size="lg">
        <ModalHeader toggle={() => !saving && setModal(null)}>
          {modal?.mode === "add" ? "Add Question" : "Edit Question"}
          <div className="text-muted small fw-normal">Unit: {unit?.unit_name}</div>
        </ModalHeader>
        <ModalBody>
          {modal ? (
            <>
              {showLecturePicker ? (
                <Field label="Lecture" required>
                  <Input
                    type="select"
                    value={modal.lectureId}
                    onChange={(e) => setModal({ ...modal, lectureId: e.target.value })}
                  >
                    {lectures.map((l) => (
                      <option key={l.lecture_id} value={l.lecture_id}>
                        {l.name}
                      </option>
                    ))}
                  </Input>
                </Field>
              ) : null}

              <QuestionEditor
                value={modal.draft}
                errors={errors}
                onChange={(draft) => setModal({ ...modal, draft })}
              />

              <div className="d-flex justify-content-end gap-2 mt-3">
                <Button color="light" onClick={() => setModal(null)} disabled={saving}>
                  Cancel
                </Button>
                <Button color="primary" onClick={submit} disabled={saving}>
                  {saving ? <Spinner size="sm" className="me-1" /> : null}
                  {modal.mode === "add" ? "Add question" : "Save changes"}
                </Button>
              </div>
            </>
          ) : null}
        </ModalBody>
      </Modal>

      <ConfirmDialog
        isOpen={!!deleteTarget}
        title="Delete question"
        message="Delete this question? This cannot be undone."
        busy={saving}
        onConfirm={confirmDelete}
        onCancel={() => setDeleteTarget(null)}
      />

      <ToastContainer />
    </React.Fragment>
  );
};

export default UnitQuestionsPage;
